import React from "react";
import styled, { css } from "styled-components";
import { useTableContext } from "@sonder/ui/source/styled/fragments/Table/Table";
import { borders, colors } from "@sonder/ui/source/styled/theme";

export interface CellFragmentProps
  extends React.InputHTMLAttributes<HTMLDivElement> {
  header?: boolean;
  alternate?: boolean;
  disabled?: boolean;
  hovered?: boolean;
  selected?: boolean;
  rowStart?: boolean;
  rowEnd?: boolean;
  firstRow?: boolean;
  lastRow?: boolean;
}
export const CellFragment = styled.div<CellFragmentProps>`
  align-items: center;
  background: ${props => {
    if (props.selected && !props.disabled) return colors.blueE;
    if (props.alternate) return colors.grayF;
    if (props.hovered && !props.disabled) return colors.white;
    return "transparent";
  }};
  box-sizing: border-box;
  display: inline-flex;
  height: 100%;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  padding: 0 0.3rem;
  padding-left: ${props => props.rowStart && "1rem"};
  padding-right: ${props => props.rowEnd && "1rem"};
  width: 100%;

  border-bottom: ${borders.standard};
  border-left: ${props => props.rowStart && borders.standard};
  border-right: ${props => props.rowEnd && borders.standard};
  border-radius: ${props => {
    if (props.rowStart && props.firstRow) return "0.3rem 0 0 0";
    if (props.rowEnd && props.firstRow) return "0 0.3rem 0 0";
    if (props.rowEnd && props.lastRow) return "0 0 0.3rem 0";
    if (props.rowStart && props.lastRow) return "0 0 0 0.3rem";
    return "0";
  }};

  ${props =>
    props.header &&
    css`
      border-top: ${borders.standard};
      color: ${colors.gray6};
    `}

  ${props =>
    props.hovered &&
    !props.disabled &&
    css`
      position: relative;
      z-index: 1;
      &:before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: -1;
        box-shadow: ${colors.shadow};
      }
    `}
`;

export interface CellProps extends CellFragmentProps {
  unread?: boolean;
  alternate?: boolean;
  rowKey?: string;
}
export const Cell = ({
  alternate,
  rowKey,
  header,
  onClick = () => undefined,
  onMouseEnter = () => undefined,
  onMouseLeave = () => undefined,
  ...cellProps
}: CellProps) => {
  const { setSelected, setHovered, selected, hovered } = useTableContext();

  return (
    <CellFragment
      {...cellProps}
      alternate={alternate}
      header={header}
      hovered={!header && rowKey === hovered}
      selected={!header && rowKey === selected}
      onClick={e => {
        if (!header && rowKey) requestAnimationFrame(() => setSelected(rowKey));
        onClick(e);
      }}
      onMouseEnter={e => {
        if (!header && rowKey) requestAnimationFrame(() => setHovered(rowKey));
        onMouseEnter(e);
      }}
      onMouseLeave={e => {
        if (!header) requestAnimationFrame(() => setHovered(null));
        onMouseLeave(e);
      }}
    />
  );
};
